<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue-sonner'

import type { MetricItem } from '@/components/tile-editor/tile-editor.vue'
import type { MergedProductRow } from '@/pages/attribution/components/product-analytics-table.vue'

import UpgradePanel from '@/components/authctrl/upgrade-panel.vue'
import { DataExporter } from '@/components/data-exporter'
import { DateRangePicker } from '@/components/date-picker'
import Page from '@/components/global-layout/basic-page.vue'
import { Icon } from '@/components/icon'
import SaveViewsButton from '@/components/reports/save-views-button.vue'
import TileEditorSaver from '@/components/tile-editor/tile-editor-saver.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useBackendFieldTranslation } from '@/composables/use-backend-field-translation'
import { useTeam } from '@/composables/use-team'
import { useTeamDateRange } from '@/composables/use-team-date-range'
import { parseSavedViewData, toReportApiScene } from '@/constants/report-library'
import { conversionViewList, productAnalyticsList, productAnalyticsListSum, productAnalyticsPeriod } from '@/services/api/stastics.api'
import { useAuthStore } from '@/stores/auth'
import { useGlobalFilterStore } from '@/stores/global-filter'
import { useUserStore } from '@/stores/user'

import AllFilterTags from '../components/all-filter-tags.vue'
import ProductAnalyticsFilter from '../components/product-analytics-filter.vue'
import ProductAnalyticsTable from '../components/product-analytics-table.vue'
import ProductAnalyticsChart from './components/product-analytics-chart.vue'

defineOptions({
  name: 'ProductAnalyticsIndex',
})

definePage({
  meta: {
    keepAlive: true,
  },
})

interface MetricColumn {
  column_name: string
  name?: string
  text?: string
  type?: string
  sortable?: boolean
}

interface HeadData {
  fixed?: Array<{ column_name: string, name?: string }>
  customized?: MetricColumn[]
}

type SortDirection = 'ascending' | 'descending' | null

interface SearchSort {
  key?: string
  sort?: SortDirection
}
interface SearchParams {
  date_start: string
  date_end: string
  timezone: string
  goal_code?: string
  model_code?: string
  search?: string
  filters: any[]
  channel?: string[]
  sorts?: SearchSort
  page: number
  page_size: number
}

interface ProductRow {
  id?: string | number
  product_id?: string | number
  product_name?: string
  [key: string]: any
}

const userStore = useUserStore()
const authStore = useAuthStore()

const isAuthLimited = computed(() => {
  const availableText = authStore.getAvailableText('product_analytics')
  return availableText !== ''
})
const globalFilter = useGlobalFilterStore()
const route = useRoute()
const { currentTeam, teamTimezone, teamCurrency } = useTeam()
const { resolveDefaultRange } = useTeamDateRange()
const { t } = useI18n()
const { translateFieldOnce } = useBackendFieldTranslation('productAnalytics')

const defaultRange = resolveDefaultRange({ type: 'relative', days: 7, includeToday: false }, { endDateOffset: 1 })

// Global Filter
const initialStart = globalFilter.dateRange?.start || defaultRange.start
const initialEnd = globalFilter.dateRange?.end || defaultRange.end

const selectedDateRange = ref({ start: initialStart, end: initialEnd })

// Sync if needed
if (!globalFilter.dateRange) {
  globalFilter.setDateRange({ start: initialStart, end: initialEnd })
}

watch(selectedDateRange, (newVal) => {
  if (newVal.start && newVal.end) {
    globalFilter.setDateRange({ ...newVal })
  }
}, { deep: true })
const compareDateRange = ref<{ start: string, end: string } | null>(null)
const pickerDraftRange = ref({ ...selectedDateRange.value })
const pickerDraftCompareRange = ref<{ start: string, end: string } | null>(null)
const isCompareMode = computed(() => Boolean(compareDateRange.value?.start && compareDateRange.value?.end))

function setSelectedDateRange(range: { start: string, end: string }) {
  selectedDateRange.value = { ...range }
  pickerDraftRange.value = { ...range }
}

function setCompareDateRange(range: { start: string, end: string } | null) {
  if (range) {
    compareDateRange.value = { ...range }
    pickerDraftCompareRange.value = { ...range }
  }
  else {
    compareDateRange.value = null
    pickerDraftCompareRange.value = null
  }
}

// Column management - Using TileEditorSaver
const COLUMN_OBJECT_KEY = 'product-analysis'
const columnConfig = ref<MetricItem[]>([])
const visibleColumnKeys = ref<string[]>([])
const tileEditorRef = ref<InstanceType<typeof TileEditorSaver> | null>(null)
const productAnalyticsChartRef = ref<InstanceType<typeof ProductAnalyticsChart> | null>(null)
const fixedColumns = ref<MetricItem[]>([])

// Default columns definition (for table header mapping)
const defaultColumns = computed(() => [
  { key: 'product_name', label: t('productAnalytics.table.product'), sortable: false },
  { key: 'conversions', label: t('productAnalytics.table.conversions'), sortable: true, type: 'number' },
  { key: 'conversion_value', label: t('productAnalytics.table.revenue'), sortable: true, type: 'currency' },
  { key: 'spend', label: t('productAnalytics.table.spend'), sortable: true, type: 'currency' },
  { key: 'cpa', label: t('productAnalytics.table.cpa'), sortable: true, type: 'currency' },
  { key: 'roas', label: t('productAnalytics.table.roas'), sortable: true, type: 'number' },
  { key: 'product_viewed', label: t('productAnalytics.table.productViews'), sortable: true, type: 'number' },
  { key: 'add_to_cart', label: t('productAnalytics.table.addToCart'), sortable: true, type: 'number' },
  { key: 'checkout_start', label: t('productAnalytics.table.checkoutStart'), sortable: true, type: 'number' },
])

const headData = computed(() => {
  if (visibleColumnKeys.value.length === 0) {
    return {
      fixed: [{ column_name: 'product_name', name: t('productAnalytics.table.product') }],
      customized: defaultColumns.value.slice(1).map(c => ({ column_name: c.key, name: c.label, type: c.type || 'string', sortable: c.sortable })),
    }
  }
  const fixedKeys = fixedColumns.value.map(m => m.column_name || String(m.id))
  const customizedKeys = visibleColumnKeys.value.filter(k => !fixedKeys.includes(k))
  return {
    fixed: fixedColumns.value.map(c => ({ column_name: c.column_name || String(c.id), name: c.name })),
    customized: customizedKeys.map((key) => {
      const fullConfig = columnConfig.value.find(m => (m.column_name || String(m.id)) === key)
      const defaultCol = defaultColumns.value.find(c => c.key === key)
      return fullConfig || (defaultCol ? { column_name: defaultCol.key, name: defaultCol.label, type: defaultCol.type || 'string', sortable: defaultCol.sortable } : null)
    }).filter(Boolean) as any[],
  }
})

const tableKey = ref(`product-${Date.now()}`)
const baseTableData = ref<ProductRow[]>([])
const pinnedTableData = ref<ProductRow[]>([])
const compareTableData = ref<ProductRow[]>([])
const totalRecords = ref(0)
const selectedRow = ref<ProductRow | null>(null)
const searchInput = ref('')
const filterShowItems = ref<string[]>([])

const MAX_PINNED_PRODUCTS = 50
const pinnedIds = ref<string[]>([])
const isApplyingSavedView = ref(false)
const appliedSavedViewCode = ref<string | null>(null)
const currentSavedViewName = ref<string | null>(null)
const pageTitleText = computed(() => currentSavedViewName.value || t('productAnalytics.pageTitle'))
const pinnedStorageKey = computed(() => {
  const teamId = currentTeam.value?.id ?? userStore.user?.team_id ?? null
  if (!teamId)
    return ''
  return `attribuly:v3:product-analytics:pins:${teamId}`
})

function normalizePinnedIds(input: unknown): string[] {
  if (!Array.isArray(input))
    return []
  const uniq = new Set<string>()
  for (const v of input) {
    const id = typeof v === 'string' || typeof v === 'number' ? String(v) : ''
    if (id)
      uniq.add(id)
  }
  return Array.from(uniq).slice(0, MAX_PINNED_PRODUCTS)
}

function loadPinnedIds() {
  const key = pinnedStorageKey.value
  if (!key)
    return
  try {
    const raw = localStorage.getItem(key)
    if (!raw) {
      pinnedIds.value = []
      return
    }
    const parsed = JSON.parse(raw)
    pinnedIds.value = normalizePinnedIds(parsed?.product_ids)
  }
  catch {
    pinnedIds.value = []
  }
}

let pinSaveTimer: number | undefined
watch(pinnedIds, (val) => {
  const key = pinnedStorageKey.value
  if (!key)
    return
  if (pinSaveTimer)
    window.clearTimeout(pinSaveTimer)
  pinSaveTimer = window.setTimeout(() => {
    try {
      localStorage.setItem(key, JSON.stringify({ version: 1, product_ids: normalizePinnedIds(val) }))
    }
    catch {}
  }, 300)
}, { deep: true })

watch(pinnedStorageKey, (key, prev) => {
  if (key && key !== prev)
    loadPinnedIds()
}, { immediate: true })

const searchParams = reactive<SearchParams>({
  date_start: selectedDateRange.value.start,
  date_end: selectedDateRange.value.end,
  timezone: defaultRange.timezone || teamTimezone.value,
  filters: [],
  sorts: { key: 'conversion_value', sort: 'descending' },
  page: 1,
  page_size: 20,
  search: '',
  model_code: globalFilter.attributionModel || undefined,
})

watch(() => globalFilter.attributionModel, (val) => {
  if (val) {
    searchParams.model_code = val
  }
})

const sumData = ref({} as Record<string, any>)
const compareSumData = ref({} as Record<string, any>)

// Search is triggered only on Enter key or button click

const normalizedListParams = computed(() => {
  const payload: Record<string, any> = {
    page: searchParams.page,
    page_size: searchParams.page_size,
    date_start: searchParams.date_start,
    date_end: searchParams.date_end,
    timezone: searchParams.timezone,
    goal_code: searchParams.goal_code,
    model_code: searchParams.model_code,
  }

  // Add search if not empty
  if (searchParams.search && searchParams.search.trim().length) {
    payload.search = searchParams.search.trim()
  }

  // Add filters - only send key, operation, values to API (not display_values, selected_products)
  if (searchParams.filters && searchParams.filters.length > 0) {
    payload.filters = searchParams.filters.map((f: any) => ({
      key: f.key,
      operation: f.operation,
      values: f.values,
    }))
  }

  // Add sorts in old system format: [{ key: 'conversion_value', sort: '2' }]
  if (searchParams.sorts?.key) {
    const sortValue = searchParams.sorts.sort === 'ascending' ? '1' : searchParams.sorts.sort === 'descending' ? '2' : undefined
    if (sortValue) {
      payload.sorts = [{ key: searchParams.sorts.key, sort: sortValue }]
    }
  }

  return payload
})

function getProductKey(row: ProductRow): string {
  const key = row.product_id ?? row.id ?? row.product_name ?? ''
  return String(key || '')
}

const pinnedKey = computed(() => pinnedIds.value.join(','))
const pinnedSet = computed(() => new Set(pinnedIds.value))

const displayCurrentTableData = computed<ProductRow[]>(() => {
  if (!pinnedIds.value.length)
    return baseTableData.value

  const pinnedByKey = new Map<string, ProductRow>()
  const pinnedOrder: ProductRow[] = []
  for (const row of pinnedTableData.value) {
    const k = getProductKey(row)
    if (!k)
      continue
    if (!pinnedByKey.has(k)) {
      pinnedByKey.set(k, row)
      pinnedOrder.push(row)
    }
  }

  if (!pinnedOrder.length)
    return baseTableData.value

  const mainDeduped = baseTableData.value.filter((row) => {
    const k = getProductKey(row)
    return k ? !pinnedByKey.has(k) : true
  })
  return [...pinnedOrder, ...mainDeduped]
})

const listQuery = useQuery({
  queryKey: computed(() => ['productAnalyticsList', normalizedListParams.value, pinnedKey.value]),
  queryFn: async () => {
    // 如果权限受限，不发起请求
    if (isAuthLimited.value) {
      return { list: { data: { records: [], total: 0 } }, sum: { data: {} }, pinnedList: null }
    }
    const baseParams = normalizedListParams.value
    const existingFilters = baseParams.filters || []
    const shouldFetchPinned = pinnedIds.value.length > 0
    const pinnedPromise = shouldFetchPinned
      ? productAnalyticsList({
          ...baseParams,
          page: 1,
          page_size: Math.max(50, pinnedIds.value.length),
          filters: [
            ...existingFilters,
            { key: 'product_id', operation: 'include', values: pinnedIds.value },
          ],
        })
      : Promise.resolve(null)

    const [listResponse, sumResponse, pinnedListResponse] = await Promise.all([
      productAnalyticsList(baseParams),
      productAnalyticsListSum(baseParams),
      pinnedPromise,
    ])

    return { list: listResponse, sum: sumResponse, pinnedList: pinnedListResponse }
  },
  placeholderData: previous => previous,
})

// Extract product_ids from current period data for comparison filter
const currentProductIds = computed(() => {
  return displayCurrentTableData.value
    .map(row => String(row.product_id || row.id || ''))
    .filter(id => id && id !== '')
})

// Comparison query - only enabled when compare mode is active and we have product_ids
const compareListQuery = useQuery({
  queryKey: computed(() => ['productAnalyticsListCompare', normalizedListParams.value, compareDateRange.value, currentProductIds.value]),
  queryFn: async () => {
    if (!compareDateRange.value || isAuthLimited.value || currentProductIds.value.length === 0) {
      return { list: { data: { records: [], total: 0 } }, sum: { data: {} } }
    }

    // Build comparison params with product_id filter from original data (for list only)
    const existingFilters = normalizedListParams.value.filters || []
    const productIdFilter = {
      key: 'product_id',
      operation: 'include',
      values: currentProductIds.value,
    }

    // List params: include product_id filter to match specific products
    const compareListParams = {
      ...normalizedListParams.value,
      date_start: compareDateRange.value.start,
      date_end: compareDateRange.value.end,
      filters: [...existingFilters, productIdFilter],
    }

    // Sum params: use original filters without product_id filter, only change date
    const compareSumParams = {
      ...normalizedListParams.value,
      date_start: compareDateRange.value.start,
      date_end: compareDateRange.value.end,
    }

    const [listResponse, sumResponse] = await Promise.all([
      productAnalyticsList(compareListParams),
      productAnalyticsListSum(compareSumParams),
    ])
    return { list: listResponse, sum: sumResponse }
  },
  enabled: computed(() => isCompareMode.value && !isAuthLimited.value && currentProductIds.value.length > 0),
  placeholderData: previous => previous,
})

watch(() => listQuery.data.value, (response) => {
  if (!response)
    return
  const records = response.list?.data?.records ?? []
  baseTableData.value = records
  pinnedTableData.value = response.pinnedList?.data?.records ?? []
  totalRecords.value = response.list?.data?.total ?? records.length
  sumData.value = response.sum?.data ?? {}
  selectedRow.value = null
  tableKey.value = `product-${Date.now()}`
}, { immediate: true })

// Watch comparison data
watch(() => compareListQuery.data.value, (response) => {
  if (!response || !isCompareMode.value) {
    compareTableData.value = []
    compareSumData.value = {}
    return
  }
  const records = response.list?.data?.records ?? []
  compareTableData.value = records
  compareSumData.value = response.sum?.data ?? {}
  tableKey.value = `product-compare-${Date.now()}`
}, { immediate: true })

// Merged table data for comparison mode
const mergedTableData = computed<MergedProductRow[]>(() => {
  if (!isCompareMode.value || compareTableData.value.length === 0) {
    // No comparison mode or no comparison data, return original data as-is
    return displayCurrentTableData.value.map(row => ({
      ...row,
      comparisonCase: 'both' as const,
      comparisonData: null,
      changeMetrics: null,
      _dimensionKey: '',
    }))
  }

  // Build merge map keyed by product_id
  const mergeMap = new Map<string, MergedProductRow>()

  // Phase 1: Process current period data
  for (const row of displayCurrentTableData.value) {
    const key = String(row.product_id || row.id || row.product_name || '')
    mergeMap.set(key, {
      ...row,
      comparisonCase: 'current-only',
      comparisonData: null,
      changeMetrics: null,
      _dimensionKey: key,
    })
  }

  // Phase 2: Process comparison period data
  for (const row of compareTableData.value) {
    const key = String(row.product_id || row.id || row.product_name || '')
    const existing = mergeMap.get(key)

    if (existing) {
      // Case: Exists in both periods
      existing.comparisonCase = 'both'
      existing.comparisonData = row
      existing.changeMetrics = calculateProductChangeMetrics(existing, row)
    }
    else {
      // Case: Only in comparison period
      mergeMap.set(key, {
        ...row,
        comparisonCase: 'comparison-only',
        comparisonData: row,
        changeMetrics: calculateProductChangeMetrics({
          conversions: 0,
          conversion_value: 0,
          spend: 0,
          cpa: 0,
          roas: 0,
        }, row),
        _dimensionKey: key,
        // Override current metrics to 0
        conversions: 0,
        conversion_value: 0,
        spend: 0,
        cpa: 0,
        roas: 0,
      })
    }
  }

  return Array.from(mergeMap.values())
})

const pinnedMergedTableData = computed<MergedProductRow[]>(() => {
  if (!pinnedIds.value.length)
    return []
  return mergedTableData.value.filter((row) => {
    const key = row._dimensionKey || getProductKey(row)
    return key ? pinnedSet.value.has(String(key)) : false
  })
})

const unpinnedMergedTableData = computed<MergedProductRow[]>(() => {
  if (!pinnedIds.value.length)
    return mergedTableData.value
  return mergedTableData.value.filter((row) => {
    const key = row._dimensionKey || getProductKey(row)
    return key ? !pinnedSet.value.has(String(key)) : true
  })
})

function handleTogglePin(payload: { productId: string, nextPinned: boolean }) {
  const productId = String(payload.productId || '')
  if (!productId)
    return

  if (payload.nextPinned) {
    if (pinnedIds.value.includes(productId))
      return
    if (pinnedIds.value.length >= MAX_PINNED_PRODUCTS) {
      toast.error(t('productAnalytics.pins.maxReached', { max: MAX_PINNED_PRODUCTS }))
      return
    }
    pinnedIds.value = [...pinnedIds.value, productId]
  }
  else {
    pinnedIds.value = pinnedIds.value.filter(id => id !== productId)
  }
}

// Calculate change metrics for product comparison
// Dynamically compute for all columns in headData
function calculateProductChangeMetrics(current: ProductRow, comparison: ProductRow): Record<string, number | null> {
  const calculateChange = (currentVal: unknown, comparisonVal: unknown): number | null => {
    const currNum = typeof currentVal === 'number' ? currentVal : Number(currentVal) || 0
    const compNum = typeof comparisonVal === 'number' ? comparisonVal : Number(comparisonVal) || 0
    if (compNum === 0)
      return null
    return ((currNum - compNum) / compNum) * 100
  }

  // Get all column names from headData (customized + fixed, skip product_name)
  const columns: string[] = [
    ...(headData.value.fixed?.map(c => c.column_name) ?? []),
    ...(headData.value.customized?.map(c => c.column_name) ?? []),
  ].filter(name => name !== 'product_name')

  const result: Record<string, number | null> = {}
  for (const col of columns) {
    result[`${col}Change`] = calculateChange(current[col], comparison[col])
  }
  return result
}

function handleSortChange({ prop, order }: { prop: string, order: SortDirection }) {
  if (!order) {
    searchParams.sorts = undefined
  }
  else {
    searchParams.sorts = { key: prop, sort: order }
  }
  searchParams.page = 1
}

function setPage(p: number) {
  const max = Math.max(1, Math.min(p, Math.ceil((totalRecords.value || 1) / searchParams.page_size)))
  searchParams.page = max
}

function handlePageSizeChange(value: number) {
  searchParams.page_size = value
  searchParams.page = 1
}

function handleSelection(rows: ProductRow[]) {
  selectedRow.value = rows && rows.length ? rows[0] : null
  refetchChart()
}

// Column management computed properties
// For TileEditorSaver - convert columnConfig to headData format
const tileEditorHeadData = computed(() => {
  // If no keys loaded yet, use defaults
  if (visibleColumnKeys.value.length === 0) {
    return defaultColumns.value.map(c => ({
      id: c.key,
      column_name: c.key,
      name: c.label,
    }))
  }

  // Map visible keys to metric items
  return visibleColumnKeys.value.map((key) => {
    // 1. Try to find in full config
    const fullConfig = columnConfig.value.find(c => (c.column_name || String(c.id)) === key)
    if (fullConfig)
      return fullConfig

    // 2. Try to find in default columns
    const defaultCol = defaultColumns.value.find(c => c.key === key)
    if (defaultCol) {
      return {
        id: defaultCol.key,
        column_name: defaultCol.key,
        name: defaultCol.label,
      }
    }

    // 3. Fallback
    return { id: key, column_name: key, name: key }
  })
})

// Column management - TileEditorSaver handlers
function handleColumnChange(data: { displayMetrics: MetricItem[], moreMetrics: MetricItem[] }) {
  const fixedKeys = fixedColumns.value.map(m => m.column_name || String(m.id))
  // Filter out fixed keys from displayMetrics to avoid duplicates if TileEditorSaver returns them
  const customizedKeys = data.displayMetrics
    .map(m => m.column_name || String(m.id))
    .filter(k => !fixedKeys.includes(k))

  visibleColumnKeys.value = [...fixedKeys, ...customizedKeys]
  columnConfig.value = [...fixedColumns.value, ...data.displayMetrics, ...data.moreMetrics]
  tableKey.value = `columns-${Date.now()}`
  refetchChart()
}

async function handleColumnDataRefresh(_object: string) {
  if (tileEditorRef.value) {
    await nextTick()
    // TileEditorSaver will auto-reload and emit loaded event
  }
}

function handleColumnLoaded(data: { fixed: MetricItem[], customized: MetricItem[], all: MetricItem[] }) {
  // 为所有列添加翻译后的 name
  const translateColumnName = (item: MetricItem): MetricItem => {
    const columnName = item.column_name || String(item.id)
    const originalName = item.name || ''
    const translated = translateFieldOnce(columnName, 'columns')

    // 如果翻译成功（translated !== columnName），使用翻译；否则使用原始 name
    const finalName = translated !== columnName ? translated : originalName

    return {
      ...item,
      name: finalName,
    }
  }

  const fixed = data.fixed.map(translateColumnName)
  let customized = data.customized.map(translateColumnName)
  const all = data.all.map(translateColumnName)

  // Ensure product_name is fixed
  const productNameKey = 'product_name'
  const isFixed = fixed.some(m => (m.column_name || String(m.id)) === productNameKey)

  if (!isFixed) {
    // Try to find in customized
    const inCustomized = customized.find(m => (m.column_name || String(m.id)) === productNameKey)
    if (inCustomized) {
      fixed.unshift(inCustomized)
      customized = customized.filter(m => (m.column_name || String(m.id)) !== productNameKey)
    }
    else {
      // Try to find in all
      const inAll = all.find(m => (m.column_name || String(m.id)) === productNameKey)
      if (inAll) {
        fixed.unshift(inAll)
      }
      else {
        // Add from default
        const def = defaultColumns.value.find(c => c.key === productNameKey)
        if (def) {
          fixed.unshift({ id: def.key, column_name: def.key, name: def.label })
        }
      }
    }
  }

  fixedColumns.value = fixed
  columnConfig.value = all
  const fixedKeys = fixed.map(m => m.column_name || String(m.id))
  const customizedKeys = customized.map(m => m.column_name || String(m.id))
  visibleColumnKeys.value = [...fixedKeys, ...customizedKeys]
  tableKey.value = `columns-loaded-${Date.now()}`
}

const chartMetrics = computed(() => (headData.value.customized ?? []).map(c => c.column_name))

const chartQueryParams = computed(() => {
  return {
    date_start: searchParams.date_start,
    date_end: searchParams.date_end,
    timezone: searchParams.timezone,
    goal_code: searchParams.goal_code,
    model_code: searchParams.model_code,
    unique_feature: selectedRow.value ? [{ dm_goods: selectedRow.value.product_id, product_name: selectedRow.value.product_name }] : null,
    metrics: chartMetrics.value,
  }
})

const chartQuery = useQuery({
  queryKey: computed(() => ['productAnalyticsPeriod', chartQueryParams.value]),
  queryFn: () => selectedRow.value ? productAnalyticsPeriod(chartQueryParams.value) : Promise.resolve({ code: 1, message: '', data: [] }),
  enabled: computed(() => Boolean(selectedRow.value)),
  placeholderData: previous => previous,
})

const chartRaw = computed<any[]>(() => chartQuery.data.value?.data ?? [])
const chartLabels = computed(() => chartRaw.value.map((e: any) => e.date || e.label || ''))
const chartDatasets = computed(() => {
  const metrics = chartMetrics.value
  return metrics.map((m, idx) => {
    const color = ['#2563eb', '#16a34a', '#dc2626', '#7c3aed', '#f59e0b', '#0ea5e9', '#ef4444', '#10b981'][idx % 8]
    const data = chartRaw.value.map((e: any) => {
      const v = e[m]
      return typeof v === 'number' ? v : Number(v) || null
    })
    return { label: m, data, borderColor: color, backgroundColor: color, tension: 0.25 } as any
  })
})

const isLoading = computed(() => listQuery.isFetching.value)
const isChartLoading = computed(() => chartQuery.isFetching.value)

// Chart 参数 - 使用 computed 确保响应式
const chartFilters = computed(() => searchParams.filters)
const chartDateStart = computed(() => searchParams.date_start)
const chartDateEnd = computed(() => searchParams.date_end)

// 手动刷新
const isManualRefreshing = ref(false)

function refetchAll() {
  listQuery.refetch()
}
function refetchChart() {
  if (selectedRow.value)
    chartQuery.refetch()
}

async function handleManualRefresh() {
  isManualRefreshing.value = true
  try {
    const promises: Promise<unknown>[] = [
      listQuery.refetch({ cancelRefetch: true }),
      chartQuery.refetch({ cancelRefetch: true }),
    ]
    // 刷新 ProductAnalyticsChart 组件内的图表
    if (productAnalyticsChartRef.value?.refetch) {
      promises.push(productAnalyticsChartRef.value.refetch())
    }
    // If compare mode is active, also refetch comparison data
    if (isCompareMode.value) {
      promises.push(compareListQuery.refetch({ cancelRefetch: true }))
    }
    await Promise.all(promises)
  }
  finally {
    isManualRefreshing.value = false
  }
}

function handleDateRangeRefresh(range: { start: string, end: string }, compareRange?: { start: string, end: string } | null) {
  // Update primary date range
  setSelectedDateRange(range)
  searchParams.date_start = range.start
  searchParams.date_end = range.end
  searchParams.page = 1

  // Update comparison date range
  if (compareRange) {
    setCompareDateRange(compareRange)
  }
  else {
    setCompareDateRange(null)
  }

  // Force immediate refetch
  nextTick(() => {
    listQuery.refetch()
    if (compareRange) {
      compareListQuery.refetch()
    }
  })
}

function handleFilterChange(filters: any[], showItems: string[]) {
  searchParams.filters = [...filters]
  filterShowItems.value = [...showItems]
  searchParams.page = 1
  // Force immediate refetch by triggering query invalidation
  nextTick(() => {
    listQuery.refetch()
  })
}

function closeFilterTag(index: number) {
  const filters = [...searchParams.filters]
  filters.splice(index, 1)
  searchParams.filters = [...filters]

  const showItems = [...filterShowItems.value]
  showItems.splice(index, 1)
  filterShowItems.value = [...showItems]

  searchParams.page = 1
  // Force immediate refetch by triggering query invalidation
  nextTick(() => {
    listQuery.refetch()
  })
}

const router = useRouter()
function showProductDetail(row: ProductRow) {
  // 参数处理：和老系统StatProductAnalytics.vue保持一致
  // 老系统只传 product_id, product_name, dateRange
  const query: Record<string, any> = {
    product_id: row.product_id ?? row.id,
    product_name: row.product_name ?? '',
    date_start: searchParams.date_start,
    date_end: searchParams.date_end,
  }
  router.push({
    path: '/attribution/product-analytics/detail',
    query: { ...query, returnTo: '/attribution/product-analytics' },
  })
}

function commitSearchImmediately() {
  searchParams.page = 1
  searchParams.search = searchInput.value.trim()
  refetchAll()
}

function getCurrentViewData() {
  return {
    selectedDateRange: selectedDateRange.value,
    compareDateRange: compareDateRange.value,
    searchParams: {
      ...searchParams,
      filters: [...searchParams.filters],
      channel: searchParams.channel ? [...searchParams.channel] : [],
    },
    filterShowItems: filterShowItems.value,
    visibleColumnKeys: visibleColumnKeys.value,
    columnConfig: columnConfig.value,
    fixedColumns: fixedColumns.value,
    pinnedIds: pinnedIds.value,
    searchInput: searchInput.value,
  }
}

function applySavedViewData(parsedData: any) {
  if (!parsedData)
    return

  if (parsedData.selectedDateRange?.start && parsedData.selectedDateRange?.end)
    setSelectedDateRange(parsedData.selectedDateRange)

  if (parsedData.compareDateRange?.start && parsedData.compareDateRange?.end)
    setCompareDateRange(parsedData.compareDateRange)
  else
    setCompareDateRange(null)

  if (parsedData.searchParams) {
    searchParams.date_start = parsedData.searchParams.date_start || searchParams.date_start
    searchParams.date_end = parsedData.searchParams.date_end || searchParams.date_end
    searchParams.timezone = parsedData.searchParams.timezone || searchParams.timezone
    searchParams.goal_code = parsedData.searchParams.goal_code || searchParams.goal_code
    searchParams.model_code = parsedData.searchParams.model_code || searchParams.model_code
    searchParams.search = parsedData.searchParams.search || ''
    searchParams.filters = Array.isArray(parsedData.searchParams.filters) ? [...parsedData.searchParams.filters] : []
    searchParams.channel = Array.isArray(parsedData.searchParams.channel) ? [...parsedData.searchParams.channel] : []
    searchParams.sorts = parsedData.searchParams.sorts || searchParams.sorts
    searchParams.page = parsedData.searchParams.page || 1
    searchParams.page_size = parsedData.searchParams.page_size || searchParams.page_size
  }

  if (Array.isArray(parsedData.filterShowItems))
    filterShowItems.value = [...parsedData.filterShowItems]
  if (Array.isArray(parsedData.visibleColumnKeys))
    visibleColumnKeys.value = [...parsedData.visibleColumnKeys]
  if (Array.isArray(parsedData.columnConfig))
    columnConfig.value = [...parsedData.columnConfig]
  if (Array.isArray(parsedData.fixedColumns))
    fixedColumns.value = [...parsedData.fixedColumns]
  if (Array.isArray(parsedData.pinnedIds))
    pinnedIds.value = [...parsedData.pinnedIds]
  searchInput.value = parsedData.searchInput || parsedData.searchParams?.search || ''

  tableKey.value = `saved-view-${Date.now()}`
}

async function applySavedViewFromQuery() {
  const viewScene = route.query.view_scene
  const viewCode = route.query.view_code
  if (viewScene !== 'product_analytics' || typeof viewCode !== 'string' || !viewCode) {
    appliedSavedViewCode.value = null
    currentSavedViewName.value = null
    return
  }
  if (appliedSavedViewCode.value === viewCode)
    return

  isApplyingSavedView.value = true
  try {
    const res = await conversionViewList({ scene: toReportApiScene('product_analytics'), page: 1, page_size: 200 })
    const records = res.data?.records || []
    const matched = records.find((r: any) => r.code === viewCode)
    if (!matched) {
      toast.error(t('conversionPath.views.notFound'))
      return
    }

    const parsed = parseSavedViewData(matched.data_json)
    if (parsed.scene !== 'product_analytics')
      return

    applySavedViewData(parsed.viewData)
    appliedSavedViewCode.value = viewCode
    currentSavedViewName.value = matched.name || null

    await Promise.all([
      listQuery.refetch({ cancelRefetch: true }),
      compareListQuery.refetch({ cancelRefetch: true }),
      chartQuery.refetch({ cancelRefetch: true }),
    ])
  }
  catch {
    toast.error(t('conversionPath.views.applyFailed'))
  }
  finally {
    isApplyingSavedView.value = false
  }
}

onMounted(() => {
  applySavedViewFromQuery()
})

watch(() => route.query, () => {
  applySavedViewFromQuery()
}, { deep: true })
</script>

<template>
  <Page :title="pageTitleText" :description="t('productAnalytics.pageDescription')" sticky header-class="!rounded-none !border-b border-b-[color:var(--p-color-border-subdued)]" class="product-analytics-page cf-fullwidth bfsp-product-analytics-span-anchor min-h-[calc(100vh-61px)] bg-[#F1F1F1] px-[--p-space-600] md:px-[--p-space-1200] p-[--p-space-600] !mt-0 !mx-0 !mb-0 !rounded-none !border-0 !bg-transparent !shadow-none !p-0 pt-[var(--p-space-400)]">
    <template #title>
      <div class="flex items-center gap-2">
        <span class="text-[color:var(--p-color-text)]">{{ pageTitleText }}</span>
        <Badge v-if="currentSavedViewName" variant="secondary">
          {{ t('saveViews.appliedBadge') }}
        </Badge>
      </div>
    </template>
    <!-- 权限受限时显示升级提示 -->
    <div v-if="isAuthLimited" class="py-12">
      <UpgradePanel auth-key="product_analytics">
        <p class="mt-4 text-base font-medium text-[color:var(--p-color-text)]">
          {{ t('productAnalytics.upgradeText') }}
        </p>
      </UpgradePanel>
    </div>

    <!-- 正常内容 -->
    <div class="space-y-6">
      <div class="flex items-center justify-end gap-3">
        <span class="bfsp-pa-saveviews-wrap"><SaveViewsButton scene="product_analytics" :get-current-view-data="getCurrentViewData" /></span>
      </div>
      <div class="md:grid md:grid-cols-1 lg:flex lg:flex-row lg:flex-nowrap lg:items-end lg:gap-x-2 mb-6">
        <div class="flex flex-col gap-1 lg:shrink-0">
          <span class="text-xs font-medium uppercase text-[color:var(--p-color-text-secondary)]">{{ t('productAnalytics.dateRangeLabel') }}</span>
          <DateRangePicker
            v-model="pickerDraftRange"
            v-model:comparison="pickerDraftCompareRange"
            class="w-full md:w-[240px]"
            :end-date-offset="1"
            :is-plan-picker="true"
            :enable-compare-mode="true"
            :enable-comparison-mode-select="true"
            :hide-calendar-by-default="true"
            auth-key="product_analytics"
            @refresh="handleDateRangeRefresh"
          />
        </div>
        <div class="flex min-w-0 flex-1 items-end gap-2">
          <div class="p-0">
            <ProductAnalyticsFilter
              v-model="searchParams.filters"
              @update:model-value="handleFilterChange"
            />
          </div>
          <TileEditorSaver
            ref="tileEditorRef"
            :object="COLUMN_OBJECT_KEY"
            :creator-id="userStore.user?.id"
            :head-data="tileEditorHeadData"
            :dialog-title="t('productAnalytics.columnDialog.title')"
            :dialog-description="t('productAnalytics.columnDialog.description')"
            translation-namespace="productAnalytics"
            @get-data="handleColumnDataRefresh"
            @change="handleColumnChange"
            @loaded="handleColumnLoaded"
          >
            <template #default="{ show }">
              <span class="bfsp-pa-editcols-wrap"><Button variant="outline" class="shadow-none !rounded-[var(--p-border-radius-100)] border-[var(--p-color-border-subdued)] bg-[var(--p-color-bg)] cta-secondary" @click="show">
                <Icon name="lucide:settings" class="mr-2 h-4 w-4" />
                {{ t('productAnalytics.buttons.columns') }}
              </Button></span>
            </template>
          </TileEditorSaver>
          <span class="bfsp-pa-export-wrap"><DataExporter type="product_analysis" :object="normalizedListParams" /></span>
          <span class="bfsp-pa-refresh-wrap"><Button
            variant="outline"
            :disabled="isManualRefreshing || isLoading || isChartLoading"
            class="shadow-none !rounded-[var(--p-border-radius-100)] border-[var(--p-color-border-subdued)] bg-[var(--p-color-bg)] cta-secondary"
            @click="handleManualRefresh"
          >
            <Icon
              name="lucide:refresh-cw"
              :class="isManualRefreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'"
            />
          </Button></span>
        </div>
      </div>

      <div v-if="filterShowItems.length > 0" class="mt-4 rounded-[var(--p-border-radius-300)] border-[var(--p-color-border-subdued)] bg-[var(--p-color-bg-surface)] p-4 shadow-[var(--p-shadow-100)]">
        <AllFilterTags :filters="filterShowItems" @close="closeFilterTag" />
      </div>

      <section class="space-y-4">
        <div class="relative rounded-[var(--p-border-radius-300)] border-[var(--p-color-border-subdued)] bg-[var(--p-color-bg-surface)] p-4 shadow-[var(--p-shadow-100)]">
          <!-- 权限受限时显示升级提示 -->
          <div v-if="isAuthLimited && displayCurrentTableData.length === 0" class="py-12">
            <UpgradePanel
              auth-key="product_analytics"
              view-event="upgrade_product_analytics_view"
              click-event="upgrade_product_analytics_click"
            >
              <p class="mt-4 text-base font-medium text-[color:var(--p-color-text)]">
                {{ t('productAnalytics.upgradeText') }}
              </p>
            </UpgradePanel>
          </div>
          <div v-else-if="isLoading && displayCurrentTableData.length === 0" class="space-y-4">
            <Skeleton class="h-10 w-full rounded-[var(--p-border-radius-100)]" />
            <Skeleton class="h-12 w-full rounded-[var(--p-border-radius-100)]" />
            <Skeleton class="h-12 w-full rounded-[var(--p-border-radius-100)]" />
            <Skeleton class="h-12 w-full rounded-[var(--p-border-radius-100)]" />
            <Skeleton class="h-12 w-full rounded-[var(--p-border-radius-100)]" />
            <Skeleton class="h-12 w-full rounded-[var(--p-border-radius-100)]" />
          </div>
          <div v-else class="relative">
            <div v-if="isLoading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/80 rounded-[var(--p-border-radius-300)]">
              <Icon name="lucide:loader-2" class="h-8 w-8 animate-spin text-[color:var(--p-color-text-success)]" />
            </div>
            <ProductAnalyticsTable
              :head-data="headData"
              :pinned-data="pinnedMergedTableData"
              :table-data="unpinnedMergedTableData"
              :table-key="tableKey"
              :search-params="searchParams"
              :sum-data="sumData"
              :pinned-ids="pinnedIds"
              :max-pins="MAX_PINNED_PRODUCTS"
              :page="searchParams.page"
              :page-size="searchParams.page_size"
              :total-records="totalRecords"
              :show-selection="false"
              :is-compare-mode="isCompareMode"
              :compare-sum-data="compareSumData"
              @on-selection-change="handleSelection"
              @on-sort-change="handleSortChange"
              @on-page-change="setPage"
              @on-page-size-change="handlePageSizeChange"
              @on-show-product-detail="showProductDetail"
              @on-pin-toggle="handleTogglePin"
            />
          </div>
        </div>
      </section>

      <section class="rounded-[var(--p-border-radius-300)] border-[var(--p-color-border-subdued)] bg-[var(--p-color-bg-surface)] shadow-[var(--p-shadow-100)]">
        <div class="flex items-center justify-between gap-4 border-b px-6 py-4">
          <h2 class="text-base font-semibold text-[color:var(--p-color-text)]">
            {{ t('productAnalytics.chart.title') }}
          </h2>
        </div>
        <div class="px-6 py-4">
          <ProductAnalyticsChart
            ref="productAnalyticsChartRef"
            :date-start="chartDateStart"
            :date-end="chartDateEnd"
            :filters="chartFilters"
            :visible="true"
          />
        </div>
      </section>
    </div>
  </Page>
</template>

<style scoped>
.product-analytics-page :deep([data-slot="card-header"]) {
  padding: var(--p-space-400) var(--p-space-400) 8px var(--p-space-400) !important;
}
.product-analytics-page :deep([data-slot="card-content"]) {
  padding: 0px var(--p-space-400) var(--p-space-400) var(--p-space-400) !important;
}

.product-analytics-page :deep([data-slot="button"]):focus-visible,
.product-analytics-page :deep([role="button"]):focus-visible,
.product-analytics-page :deep(button):focus-visible,
.product-analytics-page :deep(a[href]):focus-visible,
.product-analytics-page :deep(input):focus-visible,
.product-analytics-page :deep(select):focus-visible,
.product-analytics-page :deep(textarea):focus-visible,
.product-analytics-page :deep([tabindex]):focus-visible {
  outline: none !important;
  border-color: var(--p-color-bg-fill-success) !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep([data-slot="card-content"] [data-slot="button"]) {
  height: 36px;
  border-radius: var(--p-border-radius-100);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  background: var(--p-color-bg-fill) !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  border: 1px solid var(--p-color-border) !important;
  outline: none !important;
  transition: background-color 100ms ease-out, box-shadow 100ms ease-out;
}
.product-analytics-page :deep([data-slot="card-content"] [data-slot="button"]):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
}
.product-analytics-page :deep([data-slot="card-content"] [data-slot="button"]):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
  border-color: var(--p-color-border) !important;
}
.product-analytics-page :deep([data-slot="card-content"] [data-slot="button"]):focus-visible {
  border-color: var(--p-color-border) !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep([data-slot="card-content"] [data-slot="button"].metric2-btn-primary-success) {
  background: var(--p-color-bg-fill-success) !important;
  color: var(--p-color-text-brand-on-bg-fill) !important;
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
}
.product-analytics-page :deep([data-slot="card-content"] [data-slot="button"].metric2-btn-primary-success):hover {
  background: var(--p-color-bg-fill-success-hover) !important;
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
}
.product-analytics-page :deep([data-slot="card-content"] [data-slot="button"].metric2-btn-primary-success):active {
  background: var(--p-color-bg-fill-success-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(0, 52, 38, 0.25) inset !important;
  border: none !important;
  outline: none !important;
}
.product-analytics-page :deep([data-slot="card-content"] [data-slot="button"].metric2-btn-primary-success):focus-visible {
  border: none !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep(.cta-primary-success) {
  background: var(--p-color-bg-fill-success) !important;
  color: var(--p-color-text-brand-on-bg-fill) !important;
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
  height: 36px;
  border-radius: var(--p-border-radius-100);
  font-size: 13px;
  font-weight: 500;
  padding-left: var(--p-space-400);
  padding-right: var(--p-space-400);
}
.product-analytics-page :deep(.cta-primary-success):hover {
  background: var(--p-color-bg-fill-success-hover) !important;
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
}
.product-analytics-page :deep(.cta-primary-success):active {
  background: var(--p-color-bg-fill-success-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(0, 52, 38, 0.25) inset !important;
  border: none !important;
  outline: none !important;
}
.product-analytics-page :deep(.cta-primary-success):focus-visible {
  border: none !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep(.cta-secondary) {
  height: 36px !important;
  border-radius: var(--p-border-radius-100) !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  background: var(--p-color-bg-fill) !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  outline: none !important;
  transition: background-color 100ms ease-out, box-shadow 100ms ease-out;
  padding-left: var(--p-space-400) !important;
  padding-right: var(--p-space-400) !important;
}
.product-analytics-page :deep(.cta-secondary):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
  border-color: var(--p-color-border-subdued) !important;
}
.product-analytics-page :deep(.cta-secondary):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
  border-color: var(--p-color-border-subdued) !important;
}
.product-analytics-page :deep(.cta-secondary):focus-visible {
  border-color: var(--p-color-border-subdued) !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep(.cta-secondary-inline) {
  border-radius: var(--p-border-radius-100) !important;
  background: transparent !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  outline: none !important;
  transition: background-color 100ms ease-out, box-shadow 100ms ease-out;
}
.product-analytics-page :deep(.cta-secondary-inline):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
}
.product-analytics-page :deep(.cta-secondary-inline):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
}
.product-analytics-page :deep(.cta-secondary-inline):focus-visible {
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep(.cta-secondary-pagination) {
  height: 32px !important;
  border-radius: var(--p-border-radius-100) !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  background: var(--p-color-bg-fill) !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  outline: none !important;
  padding-left: var(--p-space-300) !important;
  padding-right: var(--p-space-300) !important;
}
.product-analytics-page :deep(.cta-secondary-pagination):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
  border-color: var(--p-color-border-subdued) !important;
}
.product-analytics-page :deep(.cta-secondary-pagination):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
  border-color: var(--p-color-border-subdued) !important;
}
.product-analytics-page :deep(.cta-secondary-pagination):focus-visible {
  border-color: var(--p-color-border-subdued) !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}
.product-analytics-page :deep(.cta-secondary-pagination):disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.product-analytics-page :deep(input) {
  height: 36px !important;
  border-radius: var(--p-border-radius-100) !important;
  background: var(--p-color-bg-fill) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  color: var(--p-color-text) !important;
  font-size: 13px !important;
}
.product-analytics-page :deep(input):focus-visible {
  outline: none !important;
  border-color: var(--p-color-bg-fill-success) !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep([data-slot="table-header"]) {
  background: var(--p-color-bg-surface-secondary) !important;
  text-transform: uppercase !important;
  letter-spacing: 0.1em !important;
  font-size: 11px !important;
  font-weight: 500 !important;
  color: var(--p-color-text-secondary) !important;
}

.product-analytics-page :deep([data-slot="table-row"]) {
  background: var(--p-color-bg-surface) !important;
}
.product-analytics-page :deep([data-slot="table-row"]):hover {
  background: var(--p-color-bg-surface-secondary) !important;
}

.product-analytics-page :deep(.text-secondary),
.product-analytics-page :deep(.text-muted-foreground) {
  color: var(--p-color-text-secondary) !important;
}

.cf-fullwidth {
  width: calc(100% + 2 * var(--p-space-600)) !important;
  position: relative !important;
  left: calc(-1 * var(--p-space-600)) !important;
  max-width: none !important;
  padding-left: var(--p-space-600) !important;
  padding-right: var(--p-space-600) !important;
  padding-top: var(--p-space-600) !important;
  padding-bottom: var(--p-space-600) !important;
  box-sizing: border-box !important;
}
@media (min-width: 768px) {
  .cf-fullwidth {
    width: calc(100% + 2 * var(--p-space-1200)) !important;
    left: calc(-1 * var(--p-space-1200)) !important;
    padding-left: var(--p-space-1200) !important;
    padding-right: var(--p-space-1200) !important;
    padding-top: var(--p-space-600) !important;
    padding-bottom: var(--p-space-600) !important;
  }
}

.product-analytics-page {
  background-color: #F1F1F1 !important;
}

.product-analytics-page :deep([data-slot="card-header"]) {
  padding: var(--p-space-400) var(--p-space-400) 8px var(--p-space-400) !important;
}
.product-analytics-page :deep([data-slot="card-content"]) {
  padding: 0px var(--p-space-400) var(--p-space-400) var(--p-space-400) !important;
}

.product-analytics-page :deep([data-slot="button"]):focus-visible,
.product-analytics-page :deep([role="button"]):focus-visible,
.product-analytics-page :deep(button):focus-visible,
.product-analytics-page :deep(a[href]):focus-visible,
.product-analytics-page :deep(input):focus-visible,
.product-analytics-page :deep(select):focus-visible,
.product-analytics-page :deep(textarea):focus-visible,
.product-analytics-page :deep([tabindex]):focus-visible {
  outline: none !important;
  border-color: var(--p-color-bg-fill-success) !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep([data-slot="card-content"] [data-slot="button"]) {
  height: 36px;
  border-radius: var(--p-border-radius-100);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  background: var(--p-color-bg-fill) !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  border: 1px solid var(--p-color-border) !important;
  outline: none !important;
  transition: background-color 100ms ease-out, box-shadow 100ms ease-out;
}
.product-analytics-page :deep([data-slot="card-content"] [data-slot="button"]):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
}
.product-analytics-page :deep([data-slot="card-content"] [data-slot="button"]):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
  border-color: var(--p-color-border) !important;
}
.product-analytics-page :deep([data-slot="card-content"] [data-slot="button"]):focus-visible {
  border-color: var(--p-color-border) !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep(.cta-primary-success) {
  background: var(--p-color-bg-fill-success) !important;
  color: var(--p-color-text-brand-on-bg-fill) !important;
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
  height: 36px;
  border-radius: var(--p-border-radius-100);
}
.product-analytics-page :deep(.cta-primary-success):hover {
  background: var(--p-color-bg-fill-success-hover) !important;
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
}
.product-analytics-page :deep(.cta-primary-success):active {
  background: var(--p-color-bg-fill-success-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(0, 52, 38, 0.25) inset !important;
  border: none !important;
  outline: none !important;
}
.product-analytics-page :deep(.cta-primary-success):focus-visible {
  border: none !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep([data-slot="table-header"]) {
  background: var(--p-color-bg-surface-secondary) !important;
  text-transform: uppercase !important;
  letter-spacing: 0.1em !important;
  font-size: 11px !important;
}

.product-analytics-page :deep([data-slot="table-row"]) {
  background: var(--p-color-bg-surface) !important;
}
.product-analytics-page :deep([data-slot="table-row"]):hover {
  background: var(--p-color-bg-surface-secondary) !important;
}

.product-analytics-page :deep(.cta-secondary) {
  height: 36px !important;
  background: var(--p-color-bg-fill) !important;
  color: var(--p-color-text) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  border-radius: var(--p-border-radius-100) !important;
  padding: 0 var(--p-space-400) !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  box-shadow: none !important;
  outline: none !important;
  transition: background-color 100ms ease-out !important;
}
.product-analytics-page :deep(.cta-secondary):hover {
  background: var(--p-color-bg-fill-hover) !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  filter: none !important;
}
.product-analytics-page :deep(.cta-secondary):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
  filter: none !important;
}
.product-analytics-page :deep(.cta-secondary):focus-visible {
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep(.cta-secondary-inline) {
  background: transparent !important;
  color: var(--p-color-text) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  border-radius: var(--p-border-radius-100) !important;
  box-shadow: none !important;
  outline: none !important;
  transition: background-color 100ms ease-out !important;
}
.product-analytics-page :deep(.cta-secondary-inline):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
  filter: none !important;
}
.product-analytics-page :deep(.cta-secondary-inline):active {
  background: var(--p-color-bg-fill-active) !important;
}
.product-analytics-page :deep(.cta-secondary-inline):focus-visible {
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep(.cta-secondary-pagination) {
  height: 32px !important;
  background: var(--p-color-bg-fill) !important;
  color: var(--p-color-text) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  border-radius: var(--p-border-radius-100) !important;
  padding: 0 var(--p-space-300) !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  box-shadow: none !important;
  outline: none !important;
}
.product-analytics-page :deep(.cta-secondary-pagination):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
  filter: none !important;
}
.product-analytics-page :deep(.cta-secondary-pagination):active {
  background: var(--p-color-bg-fill-active) !important;
}
.product-analytics-page :deep(.cta-secondary-pagination):focus-visible {
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}
.product-analytics-page :deep(.cta-secondary-pagination):disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.product-analytics-page :deep([data-slot="input"]) {
  height: 36px !important;
  background: var(--p-color-bg-surface) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  border-radius: var(--p-border-radius-100) !important;
  color: var(--p-color-text) !important;
  font-size: 14px !important;
}
.product-analytics-page :deep([data-slot="input"]):focus-visible {
  outline: none !important;
  border-color: var(--p-color-border-strong) !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep(tbody td button),
.product-analytics-page :deep(tbody td a) {
  color: var(--p-color-text-link) !important;
  text-decoration: underline;
  cursor: pointer;
}

.bfsp-product-analytics-span-anchor {
  width: calc(100% + 2* var(--p-space-600)) !important;
  position: relative !important;
  left: calc(-1 * var(--p-space-600)) !important;
  max-width: none !important;
  padding-top: var(--p-space-600) !important;
  padding-bottom: var(--p-space-600) !important;
}
@media (min-width: 768px) {
  .bfsp-product-analytics-span-anchor {
    width: calc(100% + 2*var(--p-space-1200)) !important;
    left: calc(-1*var(--p-space-1200)) !important;
    padding-top: var(--p-space-600) !important;
    padding-bottom: var(--p-space-600) !important;
  }
}

/* ================ BfS ProductAnalytics 按钮终极修 · span壳通杀 + 双锚 + 字面量 ================ */
.product-analytics-page .bfsp-pa-saveviews-wrap * { border-radius:4px !important; -webkit-border-radius:4px !important; border-top-left-radius:4px !important; border-top-right-radius:4px !important; border-bottom-left-radius:4px !important; border-bottom-right-radius:4px !important; }
.product-analytics-page .bfsp-pa-editcols-wrap * { border-radius:4px !important; -webkit-border-radius:4px !important; border-top-left-radius:4px !important; border-top-right-radius:4px !important; border-bottom-left-radius:4px !important; border-bottom-right-radius:4px !important; }
.product-analytics-page .bfsp-pa-export-wrap * { border-radius:4px !important; -webkit-border-radius:4px !important; border-top-left-radius:4px !important; border-top-right-radius:4px !important; border-bottom-left-radius:4px !important; border-bottom-right-radius:4px !important; }
.product-analytics-page .bfsp-pa-refresh-wrap * { border-radius:4px !important; -webkit-border-radius:4px !important; border-top-left-radius:4px !important; border-top-right-radius:4px !important; border-bottom-left-radius:4px !important; border-bottom-right-radius:4px !important; }

.product-analytics-page :deep(.bfsp-pa-saveviews-wrap button[data-slot=button]),
.product-analytics-page :deep(.bfsp-pa-saveviews-wrap [data-slot=button]),
.product-analytics-page :deep(.bfsp-pa-editcols-wrap button[data-slot=button]),
.product-analytics-page :deep(.bfsp-pa-editcols-wrap [data-slot=button]),
.product-analytics-page :deep(.bfsp-pa-export-wrap button[data-slot=button]),
.product-analytics-page :deep(.bfsp-pa-export-wrap [data-slot=button]),
.product-analytics-page :deep(.bfsp-pa-refresh-wrap button[data-slot=button]),
.product-analytics-page :deep(.bfsp-pa-refresh-wrap [data-slot=button]) {
  height:36px !important; border-radius:4px !important; border:1px solid var(--p-color-border-subdued) !important;
  background:#FFFFFF !important; color:#1A1A1A !important; padding:0 16px !important;
  font-size:13px !important; font-weight:500 !important; box-shadow:none !important; outline:none !important;
}
.product-analytics-page :deep(.bfsp-pa-saveviews-wrap button[data-slot=button]):hover,
.product-analytics-page :deep(.bfsp-pa-saveviews-wrap [data-slot=button]):hover,
.product-analytics-page :deep(.bfsp-pa-editcols-wrap button[data-slot=button]):hover,
.product-analytics-page :deep(.bfsp-pa-editcols-wrap [data-slot=button]):hover,
.product-analytics-page :deep(.bfsp-pa-export-wrap button[data-slot=button]):hover,
.product-analytics-page :deep(.bfsp-pa-export-wrap [data-slot=button]):hover,
.product-analytics-page :deep(.bfsp-pa-refresh-wrap button[data-slot=button]):hover,
.product-analytics-page :deep(.bfsp-pa-refresh-wrap [data-slot=button]):hover {
  background:#F1F1F1 !important; border:1px solid var(--p-color-border-subdued) !important; box-shadow:none !important; filter:none !important;
}
.product-analytics-page :deep(.bfsp-pa-saveviews-wrap button[data-slot=button]):active,
.product-analytics-page :deep(.bfsp-pa-saveviews-wrap [data-slot=button]):active,
.product-analytics-page :deep(.bfsp-pa-editcols-wrap button[data-slot=button]):active,
.product-analytics-page :deep(.bfsp-pa-editcols-wrap [data-slot=button]):active,
.product-analytics-page :deep(.bfsp-pa-export-wrap button[data-slot=button]):active,
.product-analytics-page :deep(.bfsp-pa-export-wrap [data-slot=button]):active,
.product-analytics-page :deep(.bfsp-pa-refresh-wrap button[data-slot=button]):active,
.product-analytics-page :deep(.bfsp-pa-refresh-wrap [data-slot=button]):active {
  background:#E5E7EB !important; border:1px solid var(--p-color-border-subdued) !important; box-shadow:none !important; filter:none !important;
}
.product-analytics-page :deep(.bfsp-pa-saveviews-wrap button[data-slot=button]):focus-visible,
.product-analytics-page :deep(.bfsp-pa-saveviews-wrap [data-slot=button]):focus-visible,
.product-analytics-page :deep(.bfsp-pa-editcols-wrap button[data-slot=button]):focus-visible,
.product-analytics-page :deep(.bfsp-pa-editcols-wrap [data-slot=button]):focus-visible,
.product-analytics-page :deep(.bfsp-pa-export-wrap button[data-slot=button]):focus-visible,
.product-analytics-page :deep(.bfsp-pa-export-wrap [data-slot=button]):focus-visible,
.product-analytics-page :deep(.bfsp-pa-refresh-wrap button[data-slot=button]):focus-visible,
.product-analytics-page :deep(.bfsp-pa-refresh-wrap [data-slot=button]):focus-visible {
  outline:none !important; border-color:#008060 !important;
  box-shadow:0 0 0 2px #FFFFFF,0 0 0 5px rgba(4,123,93,0.35) !important;
}
.product-analytics-page :deep([data-slot=select-trigger]),
.product-analytics-page :deep([data-slot=combobox-trigger]),
.product-analytics-page :deep([data-slot=filter-trigger]),
.product-analytics-page :deep([data-slot=popover-trigger]) {
  border-top-left-radius: 4px !important;
  border-top-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  background-color: var(--p-color-bg-surface) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  height: 36px !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
}
.product-analytics-page :deep([data-slot=toggle-group-item]) {
  border-top-left-radius: 4px !important;
  border-top-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  background-color: var(--p-color-bg-surface) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
}
.product-analytics-page :deep([data-slot=select-trigger]):hover,
.product-analytics-page :deep([data-slot=combobox-trigger]):hover,
.product-analytics-page :deep([data-slot=filter-trigger]):hover,
.product-analytics-page :deep([data-slot=popover-trigger]):hover,
.product-analytics-page :deep([data-slot=toggle-group-item]):hover {
  background-color: var(--p-color-bg) !important;
  box-shadow: none !important;
  filter: none !important;
}
.product-analytics-page :deep([data-slot=select-trigger]):focus-visible,
.product-analytics-page :deep([data-slot=combobox-trigger]):focus-visible,
.product-analytics-page :deep([data-slot=filter-trigger]):focus-visible,
.product-analytics-page :deep([data-slot=popover-trigger]):focus-visible,
.product-analytics-page :deep([data-slot=toggle-group-item]):focus-visible {
  outline: none !important;
  border-color: #008060 !important;
  box-shadow: 0 0 0 2px #FFFFFF, 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.product-analytics-page :deep(.filter-btn),
.product-analytics-page :deep(.Filter > button[data-slot=button]),
.product-analytics-page :deep(.PopoverTrigger > button[data-slot=button]),
.product-analytics-page :deep(button[role=combobox]) {
  border-top-left-radius: 4px !important;
  border-top-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  height: 36px !important;
}
.product-analytics-page :deep([role=dialog] button[data-slot=button].cta-primary-success),
.product-analytics-page :deep([role=dialog] button[data-slot=button]:last-child:not(.cta-secondary)),
.product-analytics-page :deep([role=dialog] button[type=submit]:not(.cta-secondary)) {
  background: #008060 !important;
  color: #FFFFFF !important;
  border: none !important;
  border-top-left-radius: 4px !important;
  border-top-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  height: 36px !important;
  box-shadow: none !important;
  outline: none !important;
}
.product-analytics-page :deep([role=dialog] button[data-slot=button].cta-primary-success):hover,
.product-analytics-page :deep([role=dialog] button[type=submit]:not(.cta-secondary)):hover {
  background: rgba(0, 128, 96, 0.92) !important;
  filter: none !important;
}
.product-analytics-page :deep([role=dialog] button[data-slot=button].cta-secondary),
.product-analytics-page :deep([role=dialog] button[data-slot=button][variant=outline]),
.product-analytics-page :deep([role=dialog] button[variant=outline]) {
  background: #FFFFFF !important;
  color: #1A1A1A !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  border-top-left-radius: 4px !important;
  border-top-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  height: 36px !important;
  box-shadow: none !important;
  outline: none !important;
}
.product-analytics-page :deep([role=dialog] button[data-slot=button].cta-secondary):hover,
.product-analytics-page :deep([role=dialog] button[variant=outline]):hover {
  background: var(--p-color-bg-surface-secondary) !important;
  filter: none !important;
}
.product-analytics-page :deep([role=dialog] [data-slot=select-trigger]),
.product-analytics-page :deep([role=dialog] [data-slot=combobox-trigger]),
.product-analytics-page :deep([role=dialog] [data-slot=command-input]),
.product-analytics-page :deep([role=dialog] input),
.product-analytics-page :deep([role=dialog] [data-slot=input]) {
  border-top-left-radius: 4px !important;
  border-top-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  border-color: var(--p-color-border-subdued) !important;
  background: var(--p-color-bg-surface) !important;
  height: 36px !important;
}
</style>
